'use client';

import { useCallback, useEffect, useRef } from 'react';
import bs58 from 'bs58';
import { getUserAccountPublicKeySync } from '@velocity-exchange/sdk';
import { usePrivy } from '@privy-io/react-auth';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import { usePrivyEmbeddedWalletWithFallback } from 'src/hooks/wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { isWalletBlacklistedForEmbeddedWallets } from 'src/constants/wallets';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';

const CACHE_TTL_MS = 55 * 60 * 1000; // 55 minutes (backend allows 1 hour)

export interface ApiAuthHeaders {
	'x-wallet-address': string;
	'x-signature': string;
	'x-signed-message': string;
}

interface CachedAuth {
	headers: ApiAuthHeaders;
	expiresAt: number;
}

// Module-level cache so it persists across re-renders and hook instances.
// Keyed by cacheKey (wallet + delegate scope + signed action) so headers for
// different actions (e.g. `auth` vs `requestExport`) don't evict each other.
const authCache = new Map<string, CachedAuth>();

export default function useApiAuth() {
	const { ready: privyReady, authenticated: privyAuthenticated } = usePrivy();
	const { embeddedWallet } = usePrivyWalletContext();
	const embeddedWalletPublicKey = usePrivyEmbeddedWalletWithFallback();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const authority = walletAdapter?.publicKey;
	const velocityProgramId = useVelocityStore(
		(s) => s.velocityClient.client?.program.programId
	);
	const currentUserAccount = useCurrentUserAccount();
	// Keyed by cacheKey (which includes the signed `action`) so concurrent
	// requests for different actions don't share an in-flight signing promise.
	const signingRef = useRef<Map<string, Promise<ApiAuthHeaders>>>(new Map());

	// Drop cached signing headers when the connected wallet changes or
	// disconnects, so a previous session's headers don't linger for the tab's
	// lifetime. Tracked per instance so merely mounting another `useApiAuth`
	// consumer mid-session (same wallet) doesn't wipe still-valid cached headers.
	const authorityStr = authority?.toBase58();
	const prevAuthorityRef = useRef(authorityStr);
	useEffect(() => {
		if (prevAuthorityRef.current !== authorityStr) {
			authCache.clear();
			prevAuthorityRef.current = authorityStr;
		}
	}, [authorityStr]);

	const isBlacklisted = isWalletBlacklistedForEmbeddedWallets(
		walletAdapter?.name
	);

	const delegate = currentUserAccount?.delegate;
	const isDelegated = !!delegate && !delegate.equals(DEFAULT_PUBLIC_KEY);
	// When the account is delegated, wait for embeddedWalletPublicKey to load
	// before determining whether the delegate is the embedded wallet or not.
	// Without this, a cold cache (stale browser) causes isDelegatedToNonEmbedded
	// to be true prematurely while the DB mapping query is still in flight.
	const isDelegateCheckReady = !isDelegated || !!embeddedWalletPublicKey;

	const isDelegatedToEmbeddedWallet =
		!!delegate &&
		!!embeddedWalletPublicKey &&
		delegate.equals(embeddedWalletPublicKey);
	const isDelegatedToNonEmbedded =
		isDelegated && isDelegateCheckReady && !isDelegatedToEmbeddedWallet;

	const canUseEmbeddedWallet =
		!isBlacklisted && !isDelegatedToNonEmbedded && !!currentUserAccount;

	const getAuthHeaders = useCallback(
		async (action = 'auth'): Promise<ApiAuthHeaders> => {
			if (!authority) {
				throw new Error('Wallet not connected');
			}

			const shouldUseEmbeddedWallet = canUseEmbeddedWallet && !!embeddedWallet;
			const walletAddress = shouldUseEmbeddedWallet
				? embeddedWallet.address
				: authority.toBase58();
			const velocityAuthority = currentUserAccount?.authority ?? authority;

			const subAccountId =
				currentUserAccount?.client?.getUserAccount()?.subAccountId;
			const velocityUserAccount =
				shouldUseEmbeddedWallet &&
				velocityProgramId &&
				subAccountId !== undefined &&
				walletAddress !== velocityAuthority.toBase58()
					? getUserAccountPublicKeySync(
							velocityProgramId,
							velocityAuthority,
							subAccountId
						).toString()
					: undefined;
			const delegateScope = velocityUserAccount ?? 'direct';
			const cacheKey = `${walletAddress}:${delegateScope}:${action}`;

			const cached = authCache.get(cacheKey);
			if (cached && Date.now() < cached.expiresAt) {
				return cached.headers;
			}

			const inFlight = signingRef.current.get(cacheKey);
			if (inFlight) {
				return inFlight;
			}

			const signingPromise = (async () => {
				const messagePayload: Record<string, unknown> = {
					action,
					ts: Math.floor(Date.now() / 1000),
					walletAddress,
				};

				if (velocityUserAccount) {
					messagePayload.isDelegate = true;
					messagePayload.velocityUserAccount = velocityUserAccount;
				}

				const message = JSON.stringify(messagePayload);
				const messageBytes = new TextEncoder().encode(message);

				let signatureBytes: Uint8Array;
				if (shouldUseEmbeddedWallet) {
					const result = await embeddedWallet.signMessage({
						message: messageBytes,
					});
					signatureBytes = result.signature;
				} else {
					if (!walletAdapter?.signMessage) {
						throw new Error('Wallet does not support message signing');
					}
					signatureBytes = await walletAdapter.signMessage(messageBytes);
				}

				const signatureBs58 = bs58.encode(signatureBytes);

				const headers: ApiAuthHeaders = {
					'x-wallet-address': walletAddress,
					'x-signature': signatureBs58,
					'x-signed-message': message,
				};

				authCache.set(cacheKey, {
					headers,
					expiresAt: Date.now() + CACHE_TTL_MS,
				});

				return headers;
			})();

			signingRef.current.set(cacheKey, signingPromise);

			try {
				return await signingPromise;
			} finally {
				signingRef.current.delete(cacheKey);
			}
		},
		[
			embeddedWallet,
			walletAdapter,
			authority,
			velocityProgramId,
			currentUserAccount,
			canUseEmbeddedWallet,
		]
	);

	// Non-blacklisted users: only connected once embedded wallet is loaded.
	// Blacklisted users: connected via wallet popup.
	const isConnected = canUseEmbeddedWallet
		? !!embeddedWallet
		: privyReady && !!walletAdapter?.signMessage;

	// Whether `getAuthHeaders` can actually produce a signature right now. This is
	// looser than `isConnected`: `getAuthHeaders` uses the embedded wallet when
	// present but otherwise falls back to the connected adapter, so a signing-
	// capable adapter (e.g. Phantom) can authenticate even before the embedded
	// wallet has loaded. Callers that just need to sign an API request (e.g.
	// exports) should gate on this rather than `isConnected`, which intentionally
	// waits for the embedded wallet for signless UX.
	const canSign = !!embeddedWallet || !!walletAdapter?.signMessage;

	// True when user should enable signless trading before API auth can work.
	// Gate on !privyAuthenticated so already-authenticated users don't see a
	// flash while the embedded wallet is still loading after page refresh.
	const needsSignlessSetup =
		privyReady &&
		canUseEmbeddedWallet &&
		!embeddedWallet &&
		!privyAuthenticated;

	return { getAuthHeaders, isConnected, canSign, needsSignlessSetup };
}
